// media
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCheck } from "@fortawesome/pro-regular-svg-icons";

// utils
import { cn } from "@/shared/utils";

interface PreventiveTogglePillProps {
    label: string;
    isActive: boolean;
    tier: "gold" | "platinum";
}

// Default (unselected) pill fill per tier — accent colors mirror the card header accents.
const tierAccentStyles: Record<PreventiveTogglePillProps["tier"], string> = {
    platinum: "bg-background-prevcare-b-primary",
    gold: "bg-background-prevcare-b"
};

/**
 * Presentational state indicator for the clickable preventive-care cards (A/B test).
 * The whole card is the interactive toggle control (see PreventiveCard), so this pill is
 * aria-hidden and non-interactive; its hover animation is driven by the card's `group/card`.
 * - unselected: tier-accent fill, reveals a "+" that slides in on card hover
 * - selected:   dark teal fill with a check + "Added"
 */
export function PreventiveToggleButton({ label, isActive, tier }: PreventiveTogglePillProps) {
    return (
        <span
            data-testid="clickable-card"
            aria-hidden="true"
            className={cn(
                "flex h-9 items-center justify-center overflow-hidden rounded-full px-3 py-2 text-sm font-medium text-content-primary shadow-sm transition-all duration-200 ease-out",
                isActive ? "bg-background-brand-b-primary text-content-primary-invert" : cn("text-content-primary group-hover/card:border-stroke-brand-b", tierAccentStyles[tier])
            )}
        >
            {isActive ? (
                <FontAwesomeIcon icon={faCheck} className="mr-1 size-4" />
            ) : (
                // Collapsed to zero width until the card is hovered, then slides + fades in without shifting the label.
                <span className="flex w-0 -translate-x-1 items-center overflow-hidden opacity-0 transition-all duration-200 ease-out group-hover/card:mr-1 group-hover/card:w-4 group-hover/card:translate-x-0 group-hover/card:opacity-100">
                    <FontAwesomeIcon icon={faPlus} className="size-4" />
                </span>
            )}
            <span className="whitespace-nowrap">{isActive ? "Added" : `Add ${label}`}</span>
        </span>
    );
}

export default PreventiveToggleButton;
